// src/contexts/token/TokenPriceProvider.tsx
/**
 * TokenPriceProvider - Centralizes price history data fetching
 * 
 * Fetches and provides:
 * - Token price history
 * - Benchmark prices (BTC, ETH by default)
 * - Pre-computed returns
 * - Date-aligned series for comparisons
 */

import React, { createContext, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { fetchTokensHistoryPoints } from "@/services/queries/tokens/tokensScreenerQueries";
import { toReturns } from "@/lib/metrics";
import type { TokenPriceContextValue, RawTimeSeries, PriceData } from "./types";

// ============================================================================
// CONTEXT
// ============================================================================

const TokenPriceContext = createContext<TokenPriceContextValue | null>(null);

// ============================================================================
// UTILITIES
// ============================================================================

function parseDate(d: string | number): string {
  if (typeof d === 'number') {
    // YYYYMMDD format
    if (d >= 10000101 && d <= 99991231) {
      const y = Math.floor(d / 10000);
      const m = Math.floor((d % 10000) / 100);
      const day = d % 100;
      return `${y}-${String(m).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    }
    // Unix timestamp (seconds or ms)
    const ms = d < 1e12 ? d * 1000 : d;
    return new Date(ms).toISOString().slice(0, 10);
  }
  return d.slice(0, 10);
}

function processHistoricalData(
  data: Array<{
    ticker: string;
    metrics?: Array<{
      metric_key: string;
      dates?: (string | number)[];
      values?: (number | null)[];
    }>;
  }>,
  ticker: string,
  metricKey: string = 'price_close'
): RawTimeSeries {
  const empty: RawTimeSeries = {
    rawDates: [],
    rawValues: [],
    dates: [],
    values: [],
  };
  
  const row = data.find(r => 
    (r.ticker || '').toUpperCase() === ticker.toUpperCase()
  );
  
  if (!row) return empty;
  
  const metric = row.metrics?.find(m => m.metric_key === metricKey);
  if (!metric) return empty;
  
  const rawDates = metric.dates || [];
  const rawValues = metric.values || [];
  
  const dates = rawDates.map(parseDate);
  const values = rawValues.map(v => 
    v === null || v === undefined || !Number.isFinite(Number(v)) 
      ? NaN 
      : Number(v)
  );
  
  return { rawDates, rawValues, dates, values };
}

function createPriceData(series: RawTimeSeries): PriceData {
  return {
    prices: series,
    returns: toReturns(series.values),
  };
}

// ============================================================================
// PROVIDER
// ============================================================================

interface TokenPriceProviderProps {
  ticker: string;
  benchmarks?: string[];
  children: React.ReactNode;
}

export const TokenPriceProvider: React.FC<TokenPriceProviderProps> = ({
  ticker,
  benchmarks = ['BTC', 'ETH'],
  children,
}) => {
  // Build ticker list
  const tickersCsv = useMemo(() => {
    const allTickers = new Set([ticker.toUpperCase()]);
    for (const b of benchmarks) {
      if (b.toUpperCase() !== ticker.toUpperCase()) {
        allTickers.add(b.toUpperCase());
      }
    }
    return Array.from(allTickers).join(',');
  }, [ticker, benchmarks]);
  
  // Single query for all tickers
  const query = useQuery({
    queryKey: ['token', 'price', 'history', tickersCsv],
    queryFn: async ({ signal }) => 
      fetchTokensHistoryPoints({ 
        tickersCsv, 
        metricKeysCsv: 'price_close',
        signal,
      }),
    staleTime: 60_000,
    enabled: tickersCsv.length > 0,
  });
  
  // Memoized data processing
  const processedData = useMemo(() => {
    const data = query.data || [];
    const tkr = ticker.toUpperCase();
    
    // Process main token
    const tokenSeries = processHistoricalData(data, tkr);
    const tokenData = createPriceData(tokenSeries);
    
    // Process benchmarks
    const benchmarkData: Record<string, PriceData> = {};
    for (const b of benchmarks) {
      const bTicker = b.toUpperCase();
      if (bTicker !== tkr) {
        const bSeries = processHistoricalData(data, bTicker);
        benchmarkData[bTicker] = createPriceData(bSeries);
      }
    }
    
    // Get BTC and ETH specifically
    const btcData = benchmarkData['BTC'] || createPriceData({ rawDates: [], rawValues: [], dates: [], values: [] });
    const ethData = benchmarkData['ETH'] || createPriceData({ rawDates: [], rawValues: [], dates: [], values: [] });
    
    // Align returns to common date range
    const allReturns = [
      tokenData.returns,
      btcData.returns,
      ethData.returns,
    ].filter(r => r.length > 0);
    
    const minLen = allReturns.length > 0 ? Math.min(...allReturns.map(r => r.length)) : 0;
    
    return {
      tkr,
      tokenSeries,
      tokenData,
      benchmarkData,
      btcData,
      ethData,
      minLen,
      alignedTokenReturns: tokenData.returns.slice(-minLen),
      alignedBtcReturns: btcData.returns.slice(-minLen),
      alignedEthReturns: ethData.returns.slice(-minLen),
      alignedDates: tokenSeries.dates.slice(-(minLen + 1)).slice(1),
    };
  }, [query.data, ticker, benchmarks]);
  
  const value = useMemo<TokenPriceContextValue>(() => {
    const { 
      tkr, tokenData, benchmarkData, btcData, ethData, 
      alignedDates, alignedTokenReturns, alignedBtcReturns, alignedEthReturns 
    } = processedData;
    
    return {
      ticker: tkr,
      token: tokenData,
      benchmarks: benchmarkData,
      
      aligned: {
        dates: alignedDates,
        tokenReturns: alignedTokenReturns,
        btcReturns: alignedBtcReturns,
        ethReturns: alignedEthReturns,
      },
      
      btcPrices: btcData.prices.values,
      ethPrices: ethData.prices.values,
      
      isLoading: query.isLoading,
      isError: query.isError,
      error: query.error as Error | null,
      refetch: query.refetch,
    };
  }, [processedData, query.isLoading, query.isError, query.error, query.refetch]);
  
  return (
    <TokenPriceContext.Provider value={value}>
      {children}
    </TokenPriceContext.Provider>
  );
};

export { TokenPriceContext };

